import { useState, useEffect } from "react";

import { supabase } from "~/integrations/supabase/client";

interface UseClientOrderDataReturn {
  clientTaxId: string | null;
  clientSalesConditionId: string | null;
  clientInternalNotes: string | null;
  isLoading: boolean;
}

/**
 * Hook for fetching client-related data needed in order details
 * Combines tax_id, sales_condition_id, and internal_notes fetching
 */
export function useClientOrderData(
  clientId: string | undefined
): UseClientOrderDataReturn {
  const [clientTaxId, setClientTaxId] = useState<string | null>(null);
  const [clientSalesConditionId, setClientSalesConditionId] = useState<
    string | null
  >(null);
  const [clientInternalNotes, setClientInternalNotes] = useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchClientData = async () => {
      if (!clientId) {
        setClientTaxId(null);
        setClientSalesConditionId(null);
        setClientInternalNotes(null);
        return;
      }

      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("clients")
          .select("tax_id, sales_condition_id, internal_notes")
          .eq("id", clientId)
          .single();

        if (error) {
          console.error("Error fetching client data:", error);
          setClientTaxId(null);
          setClientSalesConditionId(null);
          setClientInternalNotes(null);
        } else {
          setClientTaxId(data?.tax_id || null);
          setClientSalesConditionId(data?.sales_condition_id || null);
          setClientInternalNotes(data?.internal_notes || null);
        }
      } catch (error) {
        console.error("Error fetching client data:", error);
        setClientTaxId(null);
        setClientSalesConditionId(null);
        setClientInternalNotes(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientData();
  }, [clientId]);

  return {
    clientTaxId,
    clientSalesConditionId,
    clientInternalNotes,
    isLoading,
  };
}
